/**
 * TA-1360 (learning evidence, spec 2026-10-17 item 3). Shrunken group rates:
 * three replies out of four is not 75%. A group's rate is pulled toward a Beta
 * prior fitted over all the groups, hard when the group is small, barely when
 * it is large. Pure and deterministic. Sits beside `wilson` (learner.ts).
 */

export interface BetaPrior {
  readonly alpha: number;
  readonly beta: number;
}

export interface GroupCount {
  readonly successes: number;
  readonly n: number;
}

/**
 * The weakest prior `fitBetaPrior` returns, worth two observations. Below it
 * the shrinkage stops meaning anything; it is not a tuning knob.
 */
export const MIN_PRIOR_STRENGTH = 2;

function checkCount(successes: number, n: number): void {
  if (!Number.isSafeInteger(successes) || !Number.isSafeInteger(n) || n < 0 || successes < 0 || successes > n) {
    throw new Error(`shrinkage: ${successes} of ${n} is not a count`);
  }
}

/** (successes + alpha) / (n + alpha + beta). */
export function shrinkRate(successes: number, n: number, prior: BetaPrior): number {
  checkCount(successes, n);
  if (!(Number.isFinite(prior.alpha) && Number.isFinite(prior.beta) && prior.alpha > 0 && prior.beta > 0)) {
    throw new Error("shrinkRate: the prior needs a finite alpha and beta above 0");
  }
  return (successes + prior.alpha) / (n + prior.alpha + prior.beta);
}

/**
 * Method of moments over the groups. The mean is the pooled rate, smoothed by
 * half a success in one extra trial so it is never 0 or 1. The strength comes
 * from the spread of the group rates beyond what sampling alone explains, each
 * group weighted by its size: unweighted, one group of a single trial carried
 * a sampling term that swamped a clear spread between groups of hundreds
 * (TA-1360, the 2026-10-18 real run). With equal group sizes the weighting
 * changes nothing. The strength is
 * clamped to [MIN_PRIOR_STRENGTH, the median group size]: the prior never
 * counts for more than one typical group, so it never claims more certainty
 * than the data holds. When the median group has fewer than MIN_PRIOR_STRENGTH
 * trials, the floor wins: the prior is still worth two observations.
 */
export function fitBetaPrior(groups: readonly GroupCount[]): BetaPrior {
  for (const g of groups) checkCount(g.successes, g.n);
  const usable = groups.filter((g) => g.n > 0);
  if (usable.length === 0) throw new Error("fitBetaPrior: no group with at least one trial");
  const total = usable.reduce((s, g) => s + g.n, 0);
  const wins = usable.reduce((s, g) => s + g.successes, 0);
  const mean = (wins + 0.5) / (total + 1);
  const observed = usable.reduce((s, g) => s + g.n * (g.successes / g.n - mean) ** 2, 0) / total;
  const sampling = (mean * (1 - mean) * usable.length) / total;
  const between = observed - sampling;
  const sizes = usable.map((g) => g.n).sort((a, b) => a - b);
  const mid = Math.floor(sizes.length / 2);
  const medianN = sizes.length % 2 === 1 ? sizes[mid]! : (sizes[mid - 1]! + sizes[mid]!) / 2;
  const cap = Math.max(MIN_PRIOR_STRENGTH, medianN);
  const raw = between > 0 ? (mean * (1 - mean)) / between - 1 : cap;
  const strength = Math.min(cap, Math.max(MIN_PRIOR_STRENGTH, raw));
  return { alpha: mean * strength, beta: (1 - mean) * strength };
}
